import _isNumber from 'lodash/isNumber'
import useCourses from '@/composables/useCourses'
import { courseNameWithFallbacks } from '@/utils/entityNames'
import { capitalize } from '@/utils/generalUtils'

export default function useDegreeMaps(root, $localize = (val) => val) {
  const { getCourseUrl } = useCourses(root)

  function getCourseSetByGroupId(courseSetGroupId, courseSets) {
    return courseSets.find((set) => set.courseSetGroupId === courseSetGroupId) || {}
  }

  function getCourseByGroupId(courseGroupId, courses) {
    return (
      courses.find(
        (course) =>
          course.courseGroupId === courseGroupId ||
          course.deprecatedCourseGroupId === courseGroupId
      ) || {}
    )
  }

  function getCourseCreditMinMaxValue(courseId, creditType, courses) {
    const course = getCourseByGroupId(courseId, courses)
    const { min, max, value } =
      (course && course.credits && course.credits[creditType]) || {}
    return normalizeMinMax({ min, max, value })
  }

  function outputCreditRange({ min, max }) {
    return min === max
      ? min
      : [min, max].filter((v) => _isNumber(v)).join(' - ') || ''
  }

  function normalizeMinMax({ min, max, value }) {
    if (_isNumber(value)) return { min: value, max: value }
    if (!_isNumber(min) && _isNumber(max)) return { min: max, max }
    if (!_isNumber(max) && _isNumber(min)) return { max: min, min }
    return { min, max }
  }

  function getCourseRequirementTableObject(courseGroupId, courses) {
    const course = getCourseByGroupId(courseGroupId, courses)
    return {
      id: course.courseGroupId,
      url: getCourseUrl(course),
      name: courseNameWithFallbacks(course) || '-',
      code: course.code || course.subjectCode + course.courseNumber || '-',
      credits: outputCreditRange(
        getCourseCreditMinMaxValue(courseGroupId, 'creditHours', courses)
      ),
      type: 'courses',
    }
  }

  function getCourseSetRequirementTableObject(courseSetGroupId, courseSets) {
    const courseSet = getCourseSetByGroupId(courseSetGroupId, courseSets)
    const credits = courseSet?.credits?.creditHours || {}
    const actualCredits = courseSet?.actualCredits || {}
    return {
      id: courseSet.id,
      name: `${courseSet.name} (${capitalize($localize('course set'))})`,
      credits: outputCreditRange({...credits, ...actualCredits}),
      type: 'courseSets',
    }
  }

  return {
    getCourseSetRequirementTableObject,
    getCourseRequirementTableObject,
    outputCreditRange,
    normalizeMinMax,
  }
}
